// Firestore collection layout.
//
// Everything is under users/{uid} — a studio is a single teacher account. The
// family is the billing entity and money is an append-only log.

import { collection, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';

const root = (uid) => ['users', uid];

export const COLLECTIONS = {
  students: 'students',
  // The billing entity. Students roll up to a family; balances live here
  // conceptually but are never stored — see core/src/ledger.rs.
  families: 'families',
  // Append-only. The single source of truth for money.
  ledgerEntries: 'ledgerEntries',
  // The people on a family account, beyond the one the family document carries
  // inline (#176). One document per contact, with a `familyId`, flat rather
  // than a subcollection under the family (see `normaliseFamilyContact` in
  // src/domain/people.js): every path this app builds is
  // `users/{uid}/{collection}/{id}`, and the read store, the offline queue and
  // `useCollectionWindow` all take a single collection name.
  familyContacts: 'familyContacts',
  // Append-only. Make-up credits, same reasoning as the money ledger.
  makeupEntries: 'makeupEntries',
  // The recurrence rule. One small document per repeating lesson; occurrences
  // are expanded on demand rather than materialized, since a series may repeat
  // indefinitely.
  eventSeries: 'eventSeries',
  // Materialized only when an occurrence deviates from its series — moved,
  // re-priced, re-timed. Deterministic ID. An occurrence that was *removed* has
  // no document here: its rule date goes into the series' `exceptions` and any
  // override is deleted (#152).
  //
  // Attendance does not materialize one: the only writer is `useEvents.jsx`.
  // `attendance.occurrenceId` is `occurrenceId(seriesId, date)`, derived rather
  // than a foreign key, so it names an occurrence whether or not one was ever
  // written down.
  occurrences: 'occurrences',
  // One doc per (occurrence, student). Deterministic ID.
  attendance: 'attendance',
  // Snapshots, written server-side.
  invoices: 'invoicesV2',
  // The logo an issued invoice was drawn with (#622), one copy per logo,
  // named by its fingerprint and written server-side when the invoice is
  // frozen (functions/invoicing/freeze.js). Read one at a time, to draw a
  // frozen invoice whose logo the studio has since changed.
  invoiceLogos: 'invoiceLogos',
  // Generation queue consumed by the generateInvoice Cloud Function. Clients
  // append a request and read it back; the function writes the outcome onto the
  // same document with the admin SDK.
  invoiceRequests: 'invoiceRequests',
  // Prepaid lesson packages, as an append-only ledger. Measured in **minutes**
  // (#181); see core/src/packages.rs.
  packageEntries: 'packageEntries',
  // The reusable thing a package is sold *from*: a name, a duration, a price,
  // and an optional expiry in days from the sale's start date. Studio-level,
  // managed from Packages → Templates, and a template is **required** to sell —
  // there is no ad-hoc package, the same managed-list-with-inline-create shape
  // as categories (#110) and tags (#138).
  //
  // Nothing joins back to one. Name, price and duration are copied onto the
  // sale, which is measured rather than assumed — see src/domain/packageTemplates.js.
  packageTemplates: 'packageTemplates',
  // Expenses and non-teaching revenue, one document per entry.
  expenseEntries: 'expenseEntries',
  // The receipts on those entries, one document per attachment, with an
  // `entryId` (#183).
  //
  // Not a list on the entry document: `firestore.rules` cannot iterate, so a
  // list of maps is validated by naming every slot, and Firestore caps one
  // evaluation at 1,000 expressions — three slots fit and four do not. One
  // document per attachment gets its own evaluation budget.
  //
  // Flat with an `entryId`, not a subcollection under the entry, as
  // `familyContacts` above (see `EXPENSE_ATTACHMENTS_MAX` in
  // src/domain/expenses.js). `ExpenseRow` draws the receipt cards for every
  // row of the list, and `functions/files/reconcileStore.js` sweeps these
  // paths with an ordinary collection read; a nested shape would make both a
  // read fan-out or a collection-group query.
  expenseAttachments: 'expenseAttachments',
  // Studio-defined expense/revenue categories, added inline from the entry
  // form. The built-in list is code, not data; only additions live here.
  expenseCategories: 'expenseCategories',
  // The Online Resources library (#424): a studio's uploaded files, one row
  // per object in the `files/{uid}/{cap}/{name}` tree, and the folders they
  // sit in. Named here because the rows have a second reader besides
  // `useFiles.jsx` (the attendance drawer's Linked Online Resources). The tree
  // and what a lesson may link are core/src/resources.rs; a lesson's links are
  // `resourceIds` on its attendance record, not a collection of their own.
  files: 'files',
  resourceFolders: 'resourceFolders',
  // Event categories and locations, studio-level. Small managed lists the
  // calendar's Options menu edits.
  categories: 'categories',
  locations: 'locations',
  // Student tags, studio-level, managed from Students → Tags. A record with a
  // name, a kind, a colour, a description and — the part that makes it unlike
  // every other managed list here — **its own membership**: `studentIds` lives
  // on the tag, not `tagIds` on the student. That is MMS's model (#138) and it
  // is what makes a rename one write instead of a fan-out over the roster.
  tags: 'tags',
  // Saved lesson-note bodies, studio-level. Picked from the note editor rather
  // than retyped, and managed from the calendar's Options menu — the same
  // shape as the two lists above. See src/domain/noteTemplates.js.
  noteTemplates: 'noteTemplates',
  // Saved New Message bodies (#597): MMS's composer `Templates ▾`. A note
  // template's shape with a subject; saved and picked from the composer.
  // See src/domain/compose.js.
  messageTemplates: 'messageTemplates',
  // Practice time logged against a student. Append-only like the other logs —
  // two devices adding a session offline are two sessions, so these carry
  // random ids rather than derived ones.
  practiceEntries: 'practiceEntries',
  // Repertoire assigned to a student, one doc per piece.
  repertoire: 'repertoire',
  // The Mileage Log (#476), one document per trip, a free id. See
  // core/src/mileage.rs for the fields and why a distance is hundredths.
  mileageEntries: 'mileageEntries',
  // A report the studio ran. The client runs it (useReports.jsx) and the
  // request document carries its own result; no function consumes it. The
  // server reads one only to link its PDF (functions/documents/link.js), and
  // `deleteReportPdf` removes the PDF when the request is deleted.
  reportRequests: 'reportRequests',
  // Email and text the studio sends. Same client-appends/function-consumes
  // shape as invoiceRequests: the client appends a `queued` message, and
  // `deliverMessage` (functions/delivery/) sends it through SendGrid or Twilio
  // and writes the outcome onto the same document.
  messages: 'messages',
  // The sender's log: one row per attempt to send a message, written only by
  // the server (firestore.rules refuses every client write) and kept 400 days
  // where the outbox keeps ninety. Read by Message History for any period
  // longer than the outbox holds (#522), by a query on the student or the
  // family — never as a whole collection, which grows with every send.
  messageDeliveries: 'messageDeliveries',
  // Per-user configuration, one small document per subject rather than a
  // collection of records. Named here so the ids below are not spelled out as
  // string literals at four call sites.
  settings: 'settings',
  // What this studio's subscription to the app looks like. One document,
  // `billing/account` (see BILLING_DOCS), written only by the Stripe webhook
  // and the callable that stamps a new account. firestore.rules refuses every
  // client write to it — a browser console must not be able to award itself a
  // comp — so src/data/useEntitlement.jsx reads and never
  // writes.
  billing: 'billing',
  // The by-hand record behind that account: awarded months, comps,
  // grandfathered prices. Append-only and permanent, written by the admin CLI
  // only. Read by the client for the same reason the account is — the verdict
  // is a fold over both (domain/entitlement.js).
  billingGrants: 'billingGrants',
  // The teacher's phones, one document per install, for the push sent beside
  // every email we send the teacher (#477). **The web never reads or writes
  // it**: the phones register their own FCM tokens straight through the SDK
  // (never through the write queue — core/src/push.rs, whose contract all
  // three writers and readers spell it from), and functions/push/ sends to
  // them. Named here because this is the list of the collections a studio
  // has, which test/domain/phonePaths.test.js and firestore.rules are read
  // against.
  pushDevices: 'pushDevices',
};

// The documents under settings/. These are ids, not collections — see
// SETTINGS_DOCS below for why they are worth naming.
//
// `studio` in particular is load-bearing beyond this file: the message sender
// reads `users/{uid}/settings/studio` directly through the admin SDK
// (functions/delivery/firestoreStore.js `getStudio`), so this id and the
// field names in domain/studio.js are a contract with code that cannot import
// from here.
export const SETTINGS_DOCS = {
  studio: 'studio',
  onboarding: 'onboarding',
  // Written only by the generateInvoice Cloud Function; firestore.rules refuses
  // a client write to it.
  invoiceCounter: 'invoiceCounter',
};

// The documents under billing/, named here for the same reason `studio` is
// above: the id is a contract with code that cannot import from this file. The
// Stripe webhook and the `ensureAccount` callable both address
// `users/{uid}/billing/account` through the admin SDK.
export const BILLING_DOCS = {
  account: 'account',
};

// One of this studio's collections.
//
// **Every query built on this has to carry a `limit()`.** Not as a page size —
// nothing here paginates — but because `firestore.rules` refuses a `list` with
// no `request.query.limit`, or one above 5000 (`withinListBound`, and issue
// #64). A hook that forgets does not read a lot of documents, it reads none: it
// fails with `permission-denied` on what looks like an ordinary collection
// read. The number to use is whatever the collection's existing hook uses, at
// most 5000.
//
// Left to the call sites rather than applied here, because the limit belongs
// beside the `orderBy` and the `where` that decide *which* documents it keeps
// (`useStudents.jsx`, for one).
export function col(uid, name) {
  return collection(db, ...root(uid), name);
}

export function docRef(uid, name, id) {
  return doc(db, ...root(uid), name, id);
}
